<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import {
  getReportSummary,
  getWeeklyReport,
} from '../../../api/coLiving/reports';
import { downloadBlob } from '../../../api/coLiving/files';
import { apiErrorMessage } from '../../../api/coLiving/errors';
import { CARD_CLASS, FIELD_CLASS, SELECT_CLASS } from './constants';
import {
  REPORT_STATUS_ORDER,
  WEEKLY_FORMATS,
  formatRate,
  formatDays,
  summaryQuery,
  isRangeInvalid,
  weeklyFileName,
} from './reportHelpers';

const BREAKDOWNS = [
  { key: 'by_category', title: 'CATEGORY' },
  { key: 'by_member', title: 'MEMBER' },
  { key: 'by_site', title: 'SITE' },
];

// Ticket reports for administrators: totals and breakdowns for a period and
// filters, plus the weekly file to download. Dates are local days in the
// account timezone (the summary says which).
export default {
  name: 'CoLivingReportsPage',
  components: { PageHeader, LoadingState, BaseModal },
  data() {
    return {
      cardClass: CARD_CLASS,
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      breakdowns: BREAKDOWNS,
      statusOrder: REPORT_STATUS_ORDER,
      weeklyFormats: WEEKLY_FORMATS,
      from: '',
      to: '',
      siteId: '',
      category: '',
      memberId: '',
      weekStart: '',
      weeklyFormat: 'xlsx',
      summary: null,
      isLoading: false,
      loadError: '',
      isDownloading: false,
      weeklyOpen: false,
    };
  },
  computed: {
    ...mapGetters('coLivingCondos', { condos: 'getCondos' }),
    ...mapGetters('coLivingCatalog', { categories: 'getCategories' }),
    ...mapGetters('coLivingTeam', { members: 'getMembers' }),
    rangeInvalid() {
      return isRangeInvalid(this.from, this.to);
    },
    filters() {
      return {
        from: this.from,
        to: this.to,
        siteId: this.siteId,
        category: this.category,
        memberId: this.memberId,
      };
    },
    // Each number says which date it counts by (the report's date bases):
    // created tickets, tickets first resolved, or the day an event happened.
    tiles() {
      if (!this.summary) return [];
      const t = this.summary.totals;
      const na = this.$t('CO_LIVING.REPORTS.NA');
      const basis = key => this.$t(`CO_LIVING.REPORTS.BASIS.${key}`);
      return [
        {
          key: 'volume',
          label: this.$t('CO_LIVING.REPORTS.VOLUME'),
          value: t.volume,
          basis: basis('CREATED'),
        },
        {
          key: 'open',
          label: this.$t('CO_LIVING.REPORTS.OPEN'),
          value: t.open,
          basis: basis('CREATED_OPEN'),
        },
        {
          key: 'reopen-rate',
          label: this.$t('CO_LIVING.REPORTS.REOPEN_RATE'),
          value: formatRate(t.reopen_rate.rate) || na,
          detail: this.$t('CO_LIVING.REPORTS.REOPEN_COUNTS', {
            reopened: t.reopen_rate.reopened,
            resolved: t.reopen_rate.resolved,
          }),
          basis: basis('RESOLVED_REOPENED'),
        },
        {
          key: 'reopen-events',
          label: this.$t('CO_LIVING.REPORTS.REOPEN_EVENTS'),
          value: t.reopen_events,
          basis: basis('EVENT_REOPEN'),
        },
        {
          key: 'sla',
          label: this.$t('CO_LIVING.REPORTS.SLA_BREACHES'),
          value: t.sla_breaches.total,
          detail: this.$t('CO_LIVING.REPORTS.SLA_SPLIT', {
            ack: t.sla_breaches.ack,
            eta: t.sla_breaches.eta,
          }),
          basis: basis('EVENT_BREACH'),
        },
        {
          key: 'avg-days',
          label: this.$t('CO_LIVING.REPORTS.AVG_DAYS'),
          value: this.daysText(t.avg_days_to_close),
          basis: basis('RESOLVED_DAYS'),
        },
        {
          key: 'oldest',
          label: this.$t('CO_LIVING.REPORTS.OLDEST_OPEN'),
          value: t.oldest_open_days === null ? na : t.oldest_open_days,
          basis: basis('CREATED_OPEN'),
        },
        {
          key: 'deflection',
          label: this.$t('CO_LIVING.REPORTS.DEFLECTION'),
          value: null,
          muted: true,
          basis: basis('CHATBOT'),
        },
      ];
    },
    maxStatusCount() {
      if (!this.summary) return 1;
      return Math.max(1, ...Object.values(this.summary.by_status));
    },
  },
  mounted() {
    this.fetchCondos();
    this.fetchCategories();
    this.fetchMembers({ page_size: 100 });
    this.load();
  },
  methods: {
    ...mapActions('coLivingCondos', ['fetchCondos']),
    ...mapActions('coLivingCatalog', ['fetchCategories']),
    ...mapActions('coLivingTeam', ['fetchMembers']),
    formatRate,
    formatDays,
    async load() {
      if (this.rangeInvalid) return;
      this.isLoading = true;
      this.loadError = '';
      try {
        const { data } = await getReportSummary(summaryQuery(this.filters));
        this.summary = data;
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      } finally {
        this.isLoading = false;
      }
    },
    clearFilters() {
      this.from = '';
      this.to = '';
      this.siteId = '';
      this.category = '';
      this.memberId = '';
      this.load();
    },
    // The weekly file uses the site, category and member filters, not the
    // period above: it has its own week.
    async downloadWeekly() {
      this.isDownloading = true;
      try {
        const { siteId, category, memberId } = this.filters;
        const params = summaryQuery({ siteId, category, memberId });
        if (this.weekStart) params.week_start = this.weekStart;
        const { data } = await getWeeklyReport(params, this.weeklyFormat);
        downloadBlob(weeklyFileName(this.weekStart, this.weeklyFormat), data);
        this.weeklyOpen = false;
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.REPORTS.WEEKLY_FAILED'));
      } finally {
        this.isDownloading = false;
      }
    },
    barStyle(count) {
      return { width: `${Math.round((count / this.maxStatusCount) * 100)}%` };
    },
    reopenText(rate) {
      const pct = formatRate(rate.rate);
      return pct === null
        ? this.$t('CO_LIVING.REPORTS.NA')
        : this.$t('CO_LIVING.REPORTS.REOPEN_OF', {
            pct,
            reopened: rate.reopened,
            resolved: rate.resolved,
          });
    },
    daysText(avg) {
      const days = formatDays(avg.days);
      return days === null ? this.$t('CO_LIVING.REPORTS.NA') : days;
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto p-6" data-testid="co-living-reports-page">
    <PageHeader
      :title="$t('CO_LIVING.REPORTS.TITLE')"
      icon="arrow-trending-lines"
    />

    <p class="mb-4 text-sm text-slate-600 dark:text-slate-300">
      {{ $t('CO_LIVING.REPORTS.INTRO') }}
    </p>

    <div class="flex flex-wrap items-center gap-3 mb-2">
      <label class="flex items-center gap-2 text-sm text-slate-600">
        {{ $t('CO_LIVING.REPORTS.FROM') }}
        <input
          v-model="from"
          type="date"
          :max="to || undefined"
          class="!w-40"
          :class="[fieldClass]"
          data-testid="co-living-report-from"
          @change="load"
        />
      </label>
      <label class="flex items-center gap-2 text-sm text-slate-600">
        {{ $t('CO_LIVING.REPORTS.TO') }}
        <input
          v-model="to"
          type="date"
          :min="from || undefined"
          class="!w-40"
          :class="[fieldClass]"
          data-testid="co-living-report-to"
          @change="load"
        />
      </label>
      <select
        v-model="siteId"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-report-site"
        @change="load"
      >
        <option value="">{{ $t('CO_LIVING.TEAM.ALL_CONDOS') }}</option>
        <option v-for="condo in condos" :key="condo.id" :value="condo.id">
          {{ condo.name }}
        </option>
      </select>
      <select
        v-model="category"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-report-category"
        @change="load"
      >
        <option value="">{{ $t('CO_LIVING.TICKETS.ALL_CATEGORIES') }}</option>
        <option v-for="c in categories" :key="c.key" :value="c.key">
          {{ c.name }}
        </option>
      </select>
      <select
        v-model="memberId"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-report-member"
        @change="load"
      >
        <option value="">{{ $t('CO_LIVING.TICKETS.ALL_MEMBERS') }}</option>
        <option v-for="m in members" :key="m.id" :value="m.id">
          {{ m.name }}
        </option>
      </select>
      <woot-button
        variant="clear"
        color-scheme="secondary"
        data-testid="co-living-report-clear"
        @click="clearFilters"
      >
        {{ $t('CO_LIVING.REPORTS.CLEAR') }}
      </woot-button>
      <span class="flex-1" />
      <woot-button
        icon="arrow-download"
        color-scheme="secondary"
        data-testid="co-living-report-weekly-open"
        @click="weeklyOpen = true"
      >
        {{ $t('CO_LIVING.REPORTS.WEEKLY_BUTTON') }}
      </woot-button>
    </div>
    <p
      v-if="rangeInvalid"
      class="mb-2 text-sm text-red-500"
      data-testid="co-living-report-range-error"
    >
      {{ $t('CO_LIVING.TICKETS.RANGE_INVALID') }}
    </p>
    <p
      v-else-if="summary"
      class="mb-4 text-xs text-slate-500 dark:text-slate-400"
      data-testid="co-living-report-period"
    >
      {{
        summary.period.from || summary.period.to
          ? $t('CO_LIVING.REPORTS.PERIOD', {
              from: summary.period.from || '…',
              to: summary.period.to || '…',
              timezone: summary.period.timezone,
            })
          : $t('CO_LIVING.REPORTS.ALL_TIME', {
              timezone: summary.period.timezone,
            })
      }}
    </p>

    <LoadingState
      v-if="isLoading && !summary"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError"
      class="text-sm text-red-500"
      data-testid="co-living-load-error"
    >
      {{ loadError }}
    </p>

    <div v-else-if="summary" class="flex flex-col gap-6">
      <section
        class="grid grid-cols-2 gap-3 md:grid-cols-4"
        data-testid="co-living-report-totals"
      >
        <div
          v-for="tile in tiles"
          :key="tile.key"
          :class="[cardClass, tile.muted ? 'opacity-60' : '']"
          :data-testid="`co-living-report-${tile.key}`"
        >
          <p class="m-0 text-xs text-slate-500">{{ tile.label }}</p>
          <p
            v-if="tile.value !== null"
            class="m-0 text-2xl font-medium text-slate-900 dark:text-slate-100"
          >
            {{ tile.value }}
          </p>
          <p v-else class="m-0 text-sm text-slate-500">
            {{ $t('CO_LIVING.REPORTS.NOT_TRACKED') }}
          </p>
          <p
            v-if="tile.detail"
            class="m-0 text-xs text-slate-600 dark:text-slate-300"
          >
            {{ tile.detail }}
          </p>
          <p class="m-0 mt-1 text-xs text-slate-500 dark:text-slate-400">
            {{ tile.basis }}
          </p>
        </div>
      </section>

      <section :class="cardClass" data-testid="co-living-report-by-status">
        <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
          {{ $t('CO_LIVING.REPORTS.BY_STATUS') }}
        </h3>
        <ul class="mt-3 space-y-2 list-none">
          <li
            v-for="status in statusOrder"
            :key="status"
            class="flex items-center gap-3 text-sm"
          >
            <span class="w-32 shrink-0 text-slate-700 dark:text-slate-200">
              {{ $t(`CO_LIVING.TICKETS.STATUS.${status}`) }}
            </span>
            <span class="flex-1 h-2 rounded bg-slate-100 dark:bg-slate-700">
              <span
                class="block h-2 rounded bg-woot-500"
                :style="barStyle(summary.by_status[status] || 0)"
              />
            </span>
            <span
              class="w-8 text-right text-slate-900 dark:text-slate-100"
              :data-testid="`co-living-report-status-${status}`"
            >
              {{ summary.by_status[status] || 0 }}
            </span>
          </li>
        </ul>
      </section>

      <section
        v-for="breakdown in breakdowns"
        :key="breakdown.key"
        :class="cardClass"
        :data-testid="`co-living-report-${breakdown.key}`"
      >
        <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
          {{ $t(`CO_LIVING.REPORTS.BREAKDOWN.${breakdown.title}`) }}
        </h3>
        <p
          v-if="!summary[breakdown.key].length"
          class="mt-2 text-sm text-slate-500 dark:text-slate-400"
        >
          {{ $t('CO_LIVING.REPORTS.NO_ROWS') }}
        </p>
        <div v-else class="mt-2 overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-slate-500 dark:text-slate-400">
                <th class="py-2 pr-3">
                  {{ $t(`CO_LIVING.REPORTS.BREAKDOWN.${breakdown.title}`) }}
                </th>
                <th class="py-2 pr-3">
                  {{ $t('CO_LIVING.REPORTS.COLUMNS.CREATED') }}
                </th>
                <th class="py-2 pr-3">
                  {{ $t('CO_LIVING.REPORTS.COLUMNS.OPEN') }}
                </th>
                <th class="py-2 pr-3">
                  {{ $t('CO_LIVING.REPORTS.COLUMNS.REOPEN') }}
                </th>
                <th class="py-2">
                  {{ $t('CO_LIVING.REPORTS.COLUMNS.AVG_DAYS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in summary[breakdown.key]"
                :key="row.key || row.label"
                class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                <td class="py-2 pr-3">{{ row.label }}</td>
                <td class="py-2 pr-3">{{ row.tickets_created }}</td>
                <td class="py-2 pr-3">{{ row.open }}</td>
                <td class="py-2 pr-3">{{ reopenText(row.reopen_rate) }}</td>
                <td class="py-2">{{ daysText(row.avg_days_to_close) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <BaseModal v-if="weeklyOpen" @close="weeklyOpen = false">
      <template #title>
        {{ $t('CO_LIVING.REPORTS.WEEKLY_TITLE') }}
      </template>
      <div class="grid grid-cols-1 gap-4" data-testid="co-living-report-weekly">
        <p class="m-0 text-sm text-slate-600 dark:text-slate-300">
          {{ $t('CO_LIVING.REPORTS.WEEKLY_DESCRIPTION') }}
        </p>
        <div class="flex flex-wrap items-end gap-3">
          <div>
            <label
              for="co-living-report-week"
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('CO_LIVING.REPORTS.WEEK_OF') }}
            </label>
            <input
              id="co-living-report-week"
              v-model="weekStart"
              type="date"
              class="!w-44"
              :class="[fieldClass]"
              data-testid="co-living-report-week"
            />
          </div>
          <div>
            <label
              for="co-living-report-format"
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('CO_LIVING.REPORTS.FORMAT') }}
            </label>
            <select
              id="co-living-report-format"
              v-model="weeklyFormat"
              class="!w-28"
              :class="[selectClass]"
              data-testid="co-living-report-format"
            >
              <option v-for="f in weeklyFormats" :key="f" :value="f">
                {{ f.toUpperCase() }}
              </option>
            </select>
          </div>
        </div>
        <div class="flex justify-end gap-3">
          <woot-button
            variant="clear"
            type="button"
            @click="weeklyOpen = false"
          >
            {{ $t('CO_LIVING.COMMON.CANCEL') }}
          </woot-button>
          <woot-button
            icon="arrow-download"
            :is-loading="isDownloading"
            data-testid="co-living-report-download"
            @click="downloadWeekly"
          >
            {{ $t('CO_LIVING.REPORTS.DOWNLOAD') }}
          </woot-button>
        </div>
      </div>
    </BaseModal>
  </div>
</template>
